/* =====================================================================
   Rooster Bus — JS sticky sidebar fallback (v3 — direct viewport)
   ---------------------------------------------------------------------
   CSS `position: sticky` keeps breaking on the live storefront because
   an app injects overflow:hidden / transform / contain onto an ancestor
   element, which silently disables sticky on every descendant. CSS-only
   fixes can't reliably defeat that — the app's injection happens at
   runtime, after our stylesheet loads.

   This script translates the sidebar so it stays parked at a constant
   viewport offset as you scroll. Two key principles:

   1. Each frame we read the COLUMN's current bounding rect — that's its
      live viewport-relative top. We translate so the sidebar's top sits
      at `headerOffset`. No scroll-position math, no document-coord
      bookkeeping that could drift if the page reflows. (Earlier versions
      cached `columnPageTop` and computed against `scrollY`, which broke
      when the header CSS variable changed value mid-scroll and shifted
      the cached headerOffset.)
   2. `headerOffset` is locked at init and only re-measured on window
      resize. It never reacts to body.header-up so the header hiding /
      showing on scroll can't drag the sidebar with it.

   Re-initializes itself after Shopify section reloads (theme editor)
   and after the DynamicProductSearch AJAX swap (filter/sort changes).
   ===================================================================== */
(function () {
  'use strict';

  function rafThrottle(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        fn();
        ticking = false;
      });
    };
  }

  function measureHeaderOffset() {
    var rootStyle = window.getComputedStyle(document.documentElement);
    var cssVar = rootStyle.getPropertyValue('--header-section-height').trim();
    if (cssVar) {
      var n = parseInt(cssVar, 10);
      if (!isNaN(n) && n > 0) return n + 4;
    }
    var header = document.querySelector('.js-header, .shopify-section-header');
    if (header) return header.offsetHeight + 4;
    return 94;
  }

  function setupSticky(sidebar) {
    if (!sidebar || sidebar.__rbStickyInited) return;
    sidebar.__rbStickyInited = true;

    var column = sidebar.parentElement;
    while (column && !column.classList.contains('o-layout__item')) {
      column = column.parentElement;
    }
    if (!column) return;

    // Compositor promotion + neutralize any inherited positioning.
    sidebar.style.position = 'relative';
    sidebar.style.top = '0';
    sidebar.style.willChange = 'transform';
    sidebar.style.backfaceVisibility = 'hidden';
    sidebar.style.transform = 'translate3d(0,0,0)';

    // Locked at init. Only re-measured on window resize.
    var headerOffset = measureHeaderOffset();

    // Sidebar's natural offset within column + heights for clamping.
    // Recomputed only on resize / DOM mutation, never per-scroll-frame.
    var cache = {
      sidebarOffsetTop: 0,
      sidebarHeight: 0,
      columnHeight: 0,
      maxTranslate: 0
    };

    var lastTranslate = 0;

    function recalc() {
      // Reset transform so we measure the natural offset.
      var prev = sidebar.style.transform;
      sidebar.style.transform = 'translate3d(0,0,0)';

      cache.sidebarOffsetTop = sidebar.offsetTop;
      cache.sidebarHeight = sidebar.offsetHeight;
      cache.columnHeight = column.offsetHeight;
      cache.maxTranslate = Math.max(
        0,
        cache.columnHeight - cache.sidebarHeight - cache.sidebarOffsetTop
      );

      sidebar.style.transform = prev;

      // Force update since cache changed.
      lastTranslate = -1;
      update();
    }

    function update() {
      // Live viewport-relative top of the column. This already accounts
      // for scroll position AND any page reflow that may have happened
      // (e.g. header hiding/showing). The sidebar's transform doesn't
      // affect the column's box, so this measurement is stable regardless
      // of our previous translate.
      var columnTop = column.getBoundingClientRect().top;

      // Without transform, sidebar's viewport top would be at
      //   columnTop + cache.sidebarOffsetTop
      // We want it at `headerOffset`, so:
      var desired = headerOffset - columnTop - cache.sidebarOffsetTop;

      // Clamp: never translate up (translate < 0) and never past the
      // bottom of the column.
      var translate = desired < 0
        ? 0
        : (desired > cache.maxTranslate ? cache.maxTranslate : desired);

      if (translate !== lastTranslate) {
        sidebar.style.transform = 'translate3d(0,' + translate + 'px,0)';
        lastTranslate = translate;
      }
    }

    function onWindowResize() {
      headerOffset = measureHeaderOffset();
      recalc();
    }

    var onScroll = rafThrottle(update);
    var onResize = rafThrottle(onWindowResize);
    var onRO = rafThrottle(recalc);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(onRO);
      ro.observe(sidebar);
      ro.observe(column);
    }

    recalc();
  }

  function init() {
    if (!document.body.classList.contains('template-collection')) return;

    var sidebars = document.querySelectorAll('.collection-sidebar');
    sidebars.forEach(function (sidebar) {
      if (sidebar.closest('.mfp-content, .mfp-container')) return;
      setupSticky(sidebar);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    setTimeout(init, 0);
  });

  // Re-init after the DynamicProductSearch AJAX swap.
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type === 'childList' && m.addedNodes.length) {
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType === 1) {
            if (
              (node.classList && node.classList.contains('collection-sidebar')) ||
              (node.querySelector && node.querySelector('.collection-sidebar'))
            ) {
              setTimeout(init, 0);
              return;
            }
          }
        }
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
